import { useCallback, useEffect, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import {
  AgentEvents,
  CreateTodoListRequest,
  GetTodoListsResponse,
  InviteTodoListMemberRequest,
  InviteTodoListMemberResponse,
  RespondToTodoListInviteRequest,
  RespondToTodoListInviteResponse,
  TodoList,
  TodoListMembersResponse,
  TodoListResponse,
  TodoListUpdatedEvent,
} from '@dreamai/types';
import type { Channel } from 'pusher-js';
import api from '../../../api';

interface UseTodoListsResult {
  lists: TodoList[];
  invitations: TodoList[];
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  createList: (name: string, description?: string) => Promise<TodoList | null>;
  renameList: (listId: string, name: string) => Promise<void>;
  archiveList: (listId: string) => Promise<void>;
  /** Invite by email or phone; resolves with the server's outcome + copy. */
  inviteMember: (
    listId: string,
    recipient: string,
    name?: string,
  ) => Promise<InviteTodoListMemberResponse>;
  removeMember: (listId: string, userId: string) => Promise<void>;
  respondToInvite: (
    listId: string,
    response: 'accept' | 'decline',
  ) => Promise<RespondToTodoListInviteResponse | null>;
}

const errorMessage = (err: unknown, fallback: string): string => {
  const maybe = err as {
    response?: { data?: { error?: string } };
    message?: string;
  };
  return maybe?.response?.data?.error || maybe?.message || fallback;
};

/**
 * Loads the caller's shared todo lists + pending invitations and exposes the
 * mutations the reminders UI needs. Re-fetches on `TODO_LIST_UPDATED` Pusher
 * events so both collaborators see membership changes live.
 */
export function useTodoLists(
  userChannel: Channel | null | undefined,
  onListsChanged?: (event: TodoListUpdatedEvent) => void,
): UseTodoListsResult {
  const { getAccessTokenSilently } = useAuth0();
  const [lists, setLists] = useState<TodoList[]>([]);
  const [invitations, setInvitations] = useState<TodoList[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const authHeaders = useCallback(async () => {
    const token = await getAccessTokenSilently();
    return { headers: { Authorization: `Bearer ${token}` } };
  }, [getAccessTokenSilently]);

  const refresh = useCallback(async () => {
    try {
      const res = await api.get<GetTodoListsResponse>(
        '/todo-lists',
        await authHeaders(),
      );
      setLists(res.data.lists || []);
      setInvitations(res.data.invitations || []);
      setError(null);
    } catch (err) {
      console.error('Error fetching todo lists:', err);
      setError(errorMessage(err, 'Could not load projects'));
    } finally {
      setLoading(false);
    }
  }, [authHeaders]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  useEffect(() => {
    if (!userChannel) return;
    const handler = (event: TodoListUpdatedEvent) => {
      refresh();
      onListsChanged?.(event);
    };
    userChannel.bind(AgentEvents.TODO_LIST_UPDATED, handler);
    return () => {
      userChannel.unbind(AgentEvents.TODO_LIST_UPDATED, handler);
    };
  }, [userChannel, refresh, onListsChanged]);

  const createList = useCallback(
    async (name: string, description?: string) => {
      const body: CreateTodoListRequest = { name, description };
      try {
        const res = await api.post<TodoListResponse>(
          '/todo-lists',
          body,
          await authHeaders(),
        );
        const list = res.data.list ?? null;
        if (list) setLists((prev) => [...prev, list]);
        return list;
      } catch (err) {
        setError(errorMessage(err, 'Could not create todo list'));
        return null;
      }
    },
    [authHeaders],
  );

  const renameList = useCallback(
    async (listId: string, name: string) => {
      const res = await api.patch<TodoListResponse>(
        `/todo-lists/${listId}`,
        { name },
        await authHeaders(),
      );
      const updated = res.data.list;
      if (updated) {
        setLists((prev) => prev.map((l) => (l.id === listId ? updated : l)));
      }
    },
    [authHeaders],
  );

  const archiveList = useCallback(
    async (listId: string) => {
      await api.delete(`/todo-lists/${listId}`, await authHeaders());
      setLists((prev) => prev.filter((l) => l.id !== listId));
    },
    [authHeaders],
  );

  const applyMembers = useCallback(
    (
      listId: string,
      members: TodoListMembersResponse['members'],
      externalInvites?: TodoList['externalInvites'],
    ) => {
      if (!members) return;
      setLists((prev) =>
        prev.map((l) =>
          l.id === listId
            ? {
                ...l,
                members,
                externalInvites: externalInvites ?? l.externalInvites,
              }
            : l,
        ),
      );
    },
    [],
  );

  const inviteMember = useCallback(
    async (listId: string, recipient: string, name?: string) => {
      const body: InviteTodoListMemberRequest = {
        recipient,
        name: name?.trim() || undefined,
      };
      const res = await api.post<InviteTodoListMemberResponse>(
        `/todo-lists/${listId}/members`,
        body,
        await authHeaders(),
      );
      applyMembers(listId, res.data.members, res.data.externalInvites);
      return res.data;
    },
    [authHeaders, applyMembers],
  );

  const removeMember = useCallback(
    async (listId: string, userId: string) => {
      const res = await api.delete<TodoListMembersResponse>(
        `/todo-lists/${listId}/members/${encodeURIComponent(userId)}`,
        await authHeaders(),
      );
      applyMembers(listId, res.data.members);
    },
    [authHeaders, applyMembers],
  );

  const respondToInvite = useCallback(
    async (listId: string, response: 'accept' | 'decline') => {
      const body: RespondToTodoListInviteRequest = { response };
      try {
        const res = await api.post<RespondToTodoListInviteResponse>(
          `/todo-lists/${listId}/respond`,
          body,
          await authHeaders(),
        );
        setInvitations((prev) => prev.filter((l) => l.id !== listId));
        if (response === 'accept' && res.data.list) {
          const accepted = res.data.list;
          setLists((prev) =>
            prev.some((l) => l.id === listId)
              ? prev.map((l) => (l.id === listId ? accepted : l))
              : [...prev, accepted],
          );
        }
        return res.data;
      } catch (err) {
        setError(errorMessage(err, 'Could not respond to invitation'));
        // A 409 means the invitation was already answered elsewhere; resync.
        await refresh();
        return null;
      }
    },
    [authHeaders, refresh],
  );

  return {
    lists,
    invitations,
    loading,
    error,
    refresh,
    createList,
    renameList,
    archiveList,
    inviteMember,
    removeMember,
    respondToInvite,
  };
}
